import { useState } from "react";
import { X, Gift } from "lucide-react";
import useExitIntent from "@/hooks/useExitIntent";
import ExitIntentForm from "@/components/site/ExitIntentForm";

/**
 * ExitIntentPopup
 *
 * Thin presentational shell — exit detection lives in `useExitIntent` and
 * the form lives in `ExitIntentForm`. This keeps the component small and
 * easy to reason about.
 */
const ExitIntentPopup = () => {
  const { open, dismiss } = useExitIntent();
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/55 backdrop-blur-sm p-4 animate-in fade-in duration-300"
      onClick={dismiss}
      data-testid="exit-popup-overlay"
    >
      <div
        className="relative w-full max-w-[560px] bg-ivory rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="exit-popup-title"
        data-testid="exit-popup"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Cerrar"
          data-testid="exit-popup-close"
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-cream/80 hover:bg-charcoal hover:text-ivory flex items-center justify-center text-charcoal/70 transition-colors z-10"
        >
          <X size={18} />
        </button>

        {submitted ? <SuccessState onClose={dismiss} /> : <OfferState onSuccess={() => setSubmitted(true)} />}
      </div>
    </div>
  );
};

const OfferState = ({ onSuccess }) => (
  <div className="grid sm:grid-cols-[200px_1fr]">
    <div className="hidden sm:flex bg-gradient-to-br from-terracotta/20 via-cream to-sand/40 items-center justify-center p-8">
      <Gift size={56} className="text-terracotta" strokeWidth={1.3} />
    </div>
    <div className="p-7 sm:p-8">
      <p className="text-[10px] tracking-[0.32em] uppercase text-terracotta mb-3">Antes de irte…</p>
      <h2 id="exit-popup-title" className="font-serif text-2xl md:text-[1.7rem] text-charcoal leading-[1.15] mb-3">
        Te regalo una guía: <span className="italic">cómo prepararte para tu primera sesión</span>.
      </h2>
      <p className="text-[14.5px] text-charcoal/70 leading-relaxed mb-5">
        Para que llegues con calma a tu primera sesión de yoga oncológico: qué necesitas, qué esperar, qué <strong>no</strong> esperar. Te llega al email en 5 minutos.
      </p>
      <ExitIntentForm onSuccess={onSuccess} />
    </div>
  </div>
);

const SuccessState = ({ onClose }) => (
  <div className="p-9 text-center">
    <div className="w-14 h-14 rounded-full bg-terracotta/15 grid place-items-center mx-auto text-terracotta">
      <Gift size={26} />
    </div>
    <h2 className="font-serif text-3xl text-charcoal mt-5 mb-2">¡Hecho!</h2>
    <p className="text-charcoal/70 leading-relaxed max-w-sm mx-auto">
      Revisa tu email en los próximos minutos. Si no lo ves, mira en SPAM y márcalo como "no spam" para que las próximas cosas te lleguen bien.
    </p>
    <button
      type="button"
      onClick={onClose}
      data-testid="exit-popup-close-after"
      className="mt-6 text-sm text-charcoal border-b border-terracotta pb-1 hover:text-terracotta transition-colors"
    >
      Seguir explorando la web
    </button>
  </div>
);

export default ExitIntentPopup;
